/* DRIVER PORTAL (Phase B Increment 4) — mobile-first, black/gold. Drivers see ONLY their
   assigned trips, update status (feeds the Nanak payroll trip-clock), add notes and request
   time corrections. Route /driver. Uses client auth; a non-driver account is refused. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Plane, Flag, Check, StickyNote, Timer, Loader2, LogOut, ShieldAlert, Clock, ListChecks,
} from "lucide-react";
import { api } from "@/lib/auth";
import { useClientAuth } from "@/lib/clientAuth";
import { Eyebrow, StatusChip, RouteMap, Missing, fmtDate } from "@/components/portal/opsui";
import { DriverHours } from "@/components/portal/PayrollReports";
import { DriverCorrections } from "@/components/portal/DriverCorrections";

const STATUSES = [
  ["assigned", "Assigned to Trip", "border-gold/30 bg-obsidian-700 text-ivory"],
  ["on_the_way", "En Route to Pickup", "border-gold bg-gold/10 text-gold-light"],
  ["arrived", "Arrived at Pickup", "border-gold bg-gold/20 text-gold-light font-bold"],
  ["passenger_on_board", "Passenger On Board", "border-gold-light bg-gold text-obsidian font-black"],
  ["at_stop", "At Intermediate Stop", "border-amber-500 bg-amber-500/10 text-amber-400"],
  ["waiting", "Waiting / Standby", "border-slate-500 bg-slate-800 text-silver"],
  ["dropped_off", "Final Drop-off Done", "border-gold/40 bg-obsidian-700 text-ivory"],
  ["done", "Complete Trip", "border-ivory/40 bg-ivory/10 text-ivory"],
];

function Elapsed({ start }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(t); }, []);
  const ms = Date.now() - new Date(start).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return <span className="font-mono text-5xl font-bold tracking-tight text-ivory">{hh}:{mm}:{ss}</span>;
}

export default function DriverPortal() {
  const { user, logout } = useClientAuth();
  const authLoading = user === null;        // null = still resolving session
  const loggedOut = user === false;         // false = not signed in
  const nav = useNavigate();
  const [driver, setDriver] = useState(undefined); // undefined=loading, null=not a driver
  const [trips, setTrips] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("trip");

  useEffect(() => { if (loggedOut) nav("/login"); }, [loggedOut, nav]);
  useEffect(() => {
    if (!user) return;
    api.get("/driver/me").then((r) => setDriver(r.data)).catch(() => setDriver(null));
    api.get("/driver/trips").then((r) => { setTrips(r.data.trips); setSel((c) => c || r.data.trips[0]?.reservation_id || null); }).catch(() => {});
  }, [user]);

  const loadDetail = useCallback(() => { if (sel) api.get(`/driver/trips/${sel}`).then((r) => setDetail(r.data)).catch(() => {}); }, [sel]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const current = useMemo(() => detail?.events?.length ? detail.events[detail.events.length - 1].status : null, [detail]);
  const setStatus = async (status) => {
    try {
      await api.post(`/driver/trips/${sel}/status`, { status, ...(navigatorCoords()) });
      loadDetail();
    } catch { /* ignore */ }
  };
  const navigatorCoords = () => ({}); // real geolocation prompt wired in geofence phase; foreground-only
  const addNote = async () => { const text = window.prompt("Add an operational note"); if (text) { await api.post(`/driver/trips/${sel}/note`, { text }); } };
  const signOut = () => { logout(); nav("/login"); };

  if (authLoading || driver === undefined) return <main className="flex min-h-screen items-center justify-center bg-obsidian-900" data-testid="driver-checking"><Loader2 className="h-6 w-6 animate-spin text-gold-light" /></main>;
  if (driver === null) return (
    <main className="flex min-h-screen items-center justify-center bg-obsidian-900 px-6" data-testid="driver-forbidden">
      <div className="w-full max-w-sm rounded-3xl border border-gold/20 bg-obsidian-800 p-10 text-center"><ShieldAlert className="mx-auto h-10 w-10 text-gold" /><h1 className="mt-4 font-display text-2xl font-semibold text-ivory">Driver access only</h1><p className="mt-3 text-sm text-ivory/60">This account is not a registered driver. Contact your administrator for an invitation.</p><button onClick={signOut} data-testid="driver-forbidden-logout" className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"><LogOut className="h-4 w-4" /> Sign out</button></div>
    </main>
  );

  return (
    <div className="mx-auto min-h-screen max-w-md bg-obsidian-900 px-4 pb-24 pt-5 font-sans text-ivory" data-testid="driver-portal">
      <div className="mb-4 flex items-center justify-between">
        <div><Eyebrow>Driver</Eyebrow><p className="text-sm font-semibold text-ivory">{driver.name}</p></div>
        <button onClick={signOut} data-testid="driver-logout" className="rounded-full border border-gold/30 p-1.5 text-gold-light hover:bg-gold/10"><LogOut className="h-4 w-4" /></button>
      </div>
      <div className="mb-4 flex gap-2 overflow-x-auto text-xs">
        {[["trip", "My Trip"], ["status", "Status"], ["hours", "My Hours"], ["corrections", "Corrections"]].map(([k, l]) => (
          <button key={k} data-testid={`driver-tab-${k}`} onClick={() => setTab(k)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 ${tab === k ? "border-gold/50 bg-gold/10 text-gold-light" : "border-line text-muted-steel"}`}>{l}</button>
        ))}
      </div>

      {trips.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="driver-no-trips"><ListChecks className="h-8 w-8 text-muted-steel" /><p className="mt-3 text-sm text-ivory">No assigned trips yet</p></div>
      ) : (
        <>
          {/* running clock */}
          {detail?.clock ? (
            <div data-testid="driver-trip-clock" className="mb-4 rounded-2xl border border-ivory/20 bg-obsidian-800 p-5 text-center">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-ivory/60">Completed · {detail.clock.hours} hrs</span>
              <div className="mt-1 font-mono text-3xl font-bold text-ivory">{detail.clock.hours.toFixed(2)} hrs</div>
              <p className="mt-1 text-[10px] text-dim-charcoal">Stopped: {detail.clock.stop_reason} · trip-clock hours only</p>
            </div>
          ) : current && ["arrived", "passenger_on_board", "at_stop", "waiting", "dropped_off"].includes(current) ? (
            <div data-testid="driver-trip-clock" className="mb-4 rounded-2xl border border-gold/40 bg-gradient-to-b from-gold/10 to-obsidian-800 p-5 text-center">
              <div className="flex items-center justify-center gap-2"><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" /></span><span className="text-[11px] font-semibold uppercase tracking-widest text-gold-light">On the clock</span></div>
              <div className="mt-2"><Elapsed start={detail.events.find((e) => e.status === "arrived").at} /></div>
              <p className="mt-1 text-[10px] text-dim-charcoal">Started at Arrived · trip-clock hours only — not total compensable hours.</p>
            </div>
          ) : (
            <div className="mb-4 rounded-2xl border border-line bg-obsidian-800 p-5 text-center"><Clock className="mx-auto h-5 w-5 text-muted-steel" /><p className="mt-1 text-xs text-muted-steel">Clock starts when you mark Arrived at pickup.</p></div>
          )}

          {(tab === "trip" || tab === "status") && detail && (
            <>
              <div className="mb-4 grid grid-cols-2 gap-2">
                <button data-testid="driver-add-note" onClick={addNote} className="flex items-center justify-center gap-2 rounded-xl border border-gold/30 bg-obsidian-800 py-3 text-sm text-gold-light"><StickyNote className="h-4 w-4" /> Add Note</button>
                <button data-testid="driver-time-correction" onClick={() => setTab("corrections")} className="flex items-center justify-center gap-2 rounded-xl border border-gold/30 bg-obsidian-800 py-3 text-sm text-gold-light"><Timer className="h-4 w-4" /> Time Correction</button>
              </div>
              <div className="mb-4 rounded-2xl border border-gold/20 bg-obsidian-800 p-4">
                <div className="flex items-center justify-between"><span className="font-display text-xl font-bold text-gold-light">#{detail.confirmation_number || sel?.slice(-6)}</span><StatusChip tone="active" label={current || "assigned"} /></div>
                <p className="mt-2 text-sm text-ivory">{detail.passenger_name || "Passenger"}</p>
                <div className="mt-3 space-y-2 text-xs text-muted-steel">
                  <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold-light" /> {detail.pickup_address || "Pickup: not provided"}</p>
                  <p className="flex items-center gap-2"><Flag className="h-3.5 w-3.5 text-silver" /> {detail.dropoff_address || "Drop-off: not provided"}</p>
                  <p className="flex items-center gap-2"><Plane className="h-3.5 w-3.5 text-gold-light" /> {detail.flight || "Flight: not provided"}</p>
                </div>
              </div>
              <RouteMap className="mb-4 h-36" network={false} mobile pickup={detail.pickup_address} dropoff={detail.dropoff_address} />
            </>
          )}

          {tab === "hours" ? <DriverHours /> : tab === "corrections" ? <DriverCorrections trip={detail} /> : (
            <>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-gold-dark">Update Status</p>
              <div className="space-y-2">{STATUSES.map(([key, label, cls]) => (
                <button key={key} data-testid={`driver-status-${key}`} onClick={() => setStatus(key)} className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm transition-all ${cls} ${current === key ? "ring-2 ring-gold/40" : ""}`}><span>{label}</span>{current === key && <Check className="h-4 w-4" />}</button>
              ))}</div>
              <p className="mt-3 text-[10px] leading-relaxed text-dim-charcoal" data-testid="driver-geofence-note">Location check-in is best-effort and works only while this screen is open (no reliable background tracking). Tapping <span className="text-ivory/70">Complete Trip</span> always ends the clock reliably; a manager can also submit a time correction.</p>
            </>
          )}
        </>
      )}
    </div>
  );
}

